import { Group } from 'fabric';
import type { GroupCollectionGridConfig } from './types';
import { syncImageToNatural } from './natural-size';

// 1-3 items → 1 row, 4-6 → 2 rows, 7+ → 3 rows. Remainder goes to the earliest rows.
function decideRows(n: number): number {
  if (n <= 3) return 1;
  if (n <= 6) return 2;
  return 3;
}

function balancePerRow(n: number, rows: number): number[] {
  const base = Math.floor(n / rows);
  const remainder = n % rows;
  return Array.from({ length: rows }, (_, i) => base + (i < remainder ? 1 : 0));
}

export function arrangeGrid(group: Group, cfg: GroupCollectionGridConfig): void {
  const items = group.getObjects().filter(o => o.visible !== false);
  const count = items.length;
  if (count === 0) {
    group.set({ width: cfg.width, height: cfg.height });
    return;
  }

  const rows = decideRows(count);
  const perRow = balancePerRow(count, rows);
  const rowGap = cfg.rowGap ?? 0;
  const colGap = cfg.colGap ?? 0;
  const fitRowHeight = (cfg.height - (rows - 1) * rowGap) / rows;
  const cellHeight = cfg.maxItemHeight !== undefined ? Math.min(fitRowHeight, cfg.maxItemHeight) : fitRowHeight;
  const totalUsedHeight = rows * cellHeight + (rows - 1) * rowGap;

  let cursor = 0;
  let yCursor = -totalUsedHeight / 2 + cellHeight / 2;

  // Each item gets an equal-width slot within its row, fit-contained.
  for (const rowCount of perRow) {
    const rowItems = items.slice(cursor, cursor + rowCount);
    cursor += rowCount;

    const cellWidth = (cfg.width - (rowCount - 1) * colGap) / rowCount;
    const rowTotalWidth = rowCount * cellWidth + (rowCount - 1) * colGap;
    let xCursor = -rowTotalWidth / 2 + cellWidth / 2;

    rowItems.forEach(item => {
      syncImageToNatural(item);
      const scale = Math.min(cellWidth / (item.width || 1), cellHeight / (item.height || 1));
      item.set({
        left: xCursor,
        top: yCursor,
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale
      });
      xCursor += cellWidth + colGap;
    });

    yCursor += cellHeight + rowGap;
  }

  group.set({ width: cfg.width, height: cfg.height });
}
